import { FC, Fragment } from 'react';
import { Button, Col, Divider, Form, Row, Table } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import TextArea from 'antd/es/input/TextArea';
import FinanceModal, { useFinanceModal } from './modals/FinanceModal';
import SpecialApplicationModal, {
	useSpecialApplicationModal,
} from './modals/SpecialApplicationModal';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	getNAPBackgroundInfo,
	NAPBackgroundInfoPayload,
	NAPState,
	submitNAPBackgroundInfo,
	verifyNAPBackgroundInfo,
} from '@/libs/fleet-api/services/nap';
import { useForm } from '@/libs/antd/form';
import { LoadingOutlined } from '@ant-design/icons';
import { formatCurrency } from '@/libs/antd/formater';
import rule from '@/libs/antd/rules';
import { useUserData } from '@/libs/fleet-api/hooks';
import FleetTitle from '@/libs/antd/components/Title';
import { useModals } from '@/components/modals/ModalProvider';

const NAPApplicationBG: FC<{
	napState?: NAPState;
	prospectId: string;
	onSubmitted: () => void;
}> = ({ napState, prospectId, onSubmitted }) => {
	const { form, value, fieldName } = useForm<NAPBackgroundInfoPayload>();
	const { openPreviewModal } = useModals();
	const financeModal = useFinanceModal((type, payload) => {
		if (type === 'edit') {
			form.setFieldValue(
				fieldName('financingAndUnitRequirements'),
				value?.financingAndUnitRequirements?.map((data, index) => {
					return index === payload.index ? payload : data;
				}),
			);
		} else {
			form.setFieldValue(fieldName('financingAndUnitRequirements'), [
				...(value?.financingAndUnitRequirements ?? []),
				payload,
			]);
		}
	});

	const specialApplicationModal = useSpecialApplicationModal(
		(type, payload) => {
			if (type === 'edit') {
				form.setFieldValue(
					fieldName('specialConditionSubmission'),
					value?.specialConditionSubmission?.map((data, index) => {
						return index === payload.index ? payload : data;
					}),
				);
			} else {
				form.setFieldValue(fieldName('specialConditionSubmission'), [
					...(value?.specialConditionSubmission ?? []),
					payload,
				]);
			}
		},
	);

	const getBackgroundInfoQuery = useQuery({
		queryKey: ['getNAPBackgroundInfo', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getNAPBackgroundInfo(id);
		},
	});

	const { data } = getBackgroundInfoQuery.data ?? {};

	const submitNAPBackgroundInfoMutation = useMutation({
		mutationFn: submitNAPBackgroundInfo,
	});

	const verifyNAPBackgroundInfoMutation = useMutation({
		mutationFn: verifyNAPBackgroundInfo,
	});

	const { groupName } = useUserData() ?? {};
	const isReadOnly =
		napState?.action === 'VIEW' || napState?.action === 'DISABLED';

	if (getBackgroundInfoQuery.isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	const approverOptions = data?.finalApproverOptions ?? [];
	const statusOptions = data?.specialConditionStatusOptions ?? [];
	const subjectOptions = data?.specialConditionSubjectOptions ?? [];

	return (
		<Fragment>
			<FinanceModal {...financeModal} />
			<SpecialApplicationModal
				{...specialApplicationModal}
				approverOptions={approverOptions}
				statusOptions={statusOptions}
				subjectOptions={subjectOptions}
				prospectId={prospectId}
			/>

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				labelCol={{
					style: {
						width: 200,
					},
				}}
				onFinish={async (values) => {
					if (groupName === 'FRM') {
						await submitNAPBackgroundInfoMutation.mutateAsync(values);
					}

					if (groupName === 'CCA') {
						await verifyNAPBackgroundInfoMutation.mutateAsync(values);
					}

					onSubmitted();
				}}
			>
				<Form.Item
					name={fieldName('id')}
					initialValue={data?.id}
					noStyle
				/>
				<Form.Item
					name={fieldName('napId')}
					initialValue={data?.napId}
					noStyle
				/>
				<Form.Item
					name={fieldName('prospectId')}
					initialValue={prospectId}
					noStyle
				/>
				<Form.Item
					name={fieldName('specialConditionSubmission')}
					initialValue={data?.specialConditionSubmission}
					noStyle
				/>

				<FleetTitle>
					Kebutuhan Pembiayaan dan Penggunaan Unit{' '}
					<span style={{ color: '#E10101' }}>*</span>
				</FleetTitle>
				<Row style={{ marginBlock: 20 }}>
					<Col flex="auto" />
					<Col flex="none">
						<Button
							style={{
								borderRadius: 50,
								fontSize: 12,
								height: 24,
								fontWeight: 700,
								...(!isReadOnly && {
									borderColor: '#1557BC',
									color: '#1557BC',
								}),
							}}
							icon={'+'}
							disabled={isReadOnly}
							onClick={() => financeModal.open({ type: 'new' })}
						>
							Add Another
						</Button>
					</Col>
				</Row>

				<Form.Item
					name={fieldName('financingAndUnitRequirements')}
					initialValue={data?.financingAndUnitRequirements}
					wrapperCol={{ span: 24 }}
					labelCol={{ span: 0 }}
					rules={[rule.array()]}
				>
					<Table
						bordered
						pagination={false}
						dataSource={
							value?.financingAndUnitRequirements?.map?.((data, i) => {
								return {
									...data,
									key: i + 1,
								};
							}) ?? []
						}
						columns={[
							{
								title: 'Action',
								colSpan: 2,
								width: 100,
								align: 'center',
								render: (_, record, index) => (
									<a
										style={isReadOnly ? { color: '#bababa' } : {}}
										onClick={(e) => {
											e.preventDefault();
											if (!isReadOnly) {
												financeModal.open({
													type: 'edit',
													data: { ...record, index },
												});
											}
										}}
									>
										<EditOutlined />
									</a>
								),
							},
							{
								colSpan: 0,
								width: 100,
								align: 'center',
								render: (_, __, index) => (
									<a
										style={isReadOnly ? { color: '#bababa' } : {}}
										onClick={(e) => {
											e.preventDefault();
											if (!isReadOnly) {
												form.setFieldValue(
													fieldName('financingAndUnitRequirements'),
													value?.financingAndUnitRequirements?.filter(
														(_, i) => index !== i,
													),
												);
											}
										}}
									>
										<DeleteOutlined />
									</a>
								),
							},
							{
								title: 'Jumlah Unit',
								render: (_, record) => record?.numberOfUnits,
							},
							{
								title: 'Detail Merk Unit',
								render: (_, record) => record?.unitBrandDetails,
							},
							{
								title: 'Harga',
								render: (_, record) =>
									record?.price && formatCurrency(record.price),
							},
							{
								title: 'Lokasi Project',
								render: (_, record) => record?.projectLocation,
							},
						]}
					/>
				</Form.Item>

				<Divider />

				<Row style={{ marginBottom: 20 }}>
					<Col flex="auto">
						<strong>
							Informasi History Approval Kondisi Khusus (Jika Ada)
						</strong>
					</Col>
				</Row>

				<Table
					bordered
					pagination={false}
					dataSource={[]}
					columns={[
						{
							title: 'Pipeline ID',
							render: () => '-',
						},
						{
							title: 'Perihal',
							render: () => '-',
						},
						{
							title: 'Ketentuan',
							render: () => '-',
						},
						{
							title: 'Tanggal Disetujui',
							render: () => '-',
						},
						{
							title: 'Usulan',
							render: () => '-',
						},
						{
							title: 'Pemutus Terakhir',
							render: () => '-',
						},
					]}
				/>

				<Divider />

				<Row style={{ marginBottom: 20 }}>
					<Col flex="auto">
						<strong>Informasi Pengajuan Kondisi Khusus (Jika Ada)</strong>
					</Col>
					<Col flex="none">
						<Button
							style={{
								borderRadius: 50,
								fontSize: 12,
								height: 24,
								fontWeight: 700,
								...(!isReadOnly && {
									borderColor: '#1557BC',
									color: '#1557BC',
								}),
							}}
							icon={'+'}
							disabled={isReadOnly}
							onClick={() => specialApplicationModal.open({ type: 'new' })}
						>
							Add Another
						</Button>
					</Col>
				</Row>

				<Table
					bordered
					pagination={false}
					dataSource={
						value?.specialConditionSubmission?.map((data, key) => {
							return {
								...data,
								key,
							};
						}) ?? []
					}
					columns={[
						{
							title: 'Action',
							colSpan: 2,
							width: 100,
							align: 'center',
							render: (_, record, index) => (
								<a
									style={isReadOnly ? { color: '#bababa' } : {}}
									onClick={(e) => {
										e.preventDefault();
										if (!isReadOnly) {
											specialApplicationModal.open({
												type: 'edit',
												data: { ...record, index },
											});
										}
									}}
								>
									<EditOutlined />
								</a>
							),
						},
						{
							colSpan: 0,
							width: 100,
							align: 'center',
							render: (_, __, index) => (
								<a
									style={isReadOnly ? { color: '#bababa' } : {}}
									onClick={(e) => {
										e.preventDefault();
										if (!isReadOnly) {
											form.setFieldValue(
												fieldName('specialConditionSubmission'),
												value?.specialConditionSubmission?.filter(
													(_, i) => index !== i,
												),
											);
										}
									}}
								>
									<DeleteOutlined />
								</a>
							),
						},
						{
							title: 'Perihal',
							render: (_, record) =>
								subjectOptions.find((data) => data.id === record.subjectId)
									?.value,
						},
						{
							title: 'Ketentuan',
							render: (_, record) => record.provisions,
						},
						{
							title: 'Usulan',
							render: (_, record) => record.proposal,
						},
						{
							title: 'Pemutus Terakhir',
							render: (_, record) => (
								<ul style={{ margin: 0, paddingLeft: 16 }}>
									{record.finalApprover.map((id) => {
										return (
											<li>
												{approverOptions.find((option) => option.id === id)
													?.value ?? id}
											</li>
										);
									})}
								</ul>
							),
						},
						{
							title: 'Pengajuan Kondisi Khusus',
							render: (_, record) => record.specialCondition,
						},
						{
							title: 'Status Pengajuan Kondisi Khusus',
							render: (_, record) =>
								statusOptions.find(
									(data) => data.id === record.specialConditionStatusId,
								)?.value,
						},
						{
							title: 'Evidence',
							render: (_, record) => {
								if (record.evidenceFile) {
									return (
										<Button
											type="link"
											onClick={() => {
												openPreviewModal(record.evidenceFile);
											}}
										>
											Evidence
										</Button>
									);
								}
								return null;
							},
						},
					]}
				/>

				<Form.Item
					name={fieldName('notes')}
					label={groupName === 'FRM' ? 'Informasi Tambahan' : 'Notes FRM'}
					style={{ marginTop: 20 }}
					initialValue={data?.notes}
					rules={groupName === 'FRM' ? [rule.required()] : []}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly || groupName !== 'FRM'}
					/>
				</Form.Item>
				<Divider />
				{groupName !== 'FRM' && (
					<Form.Item
						label={groupName === 'CCA' ? 'Verification Notes' : 'Notes CCA'}
						style={{ marginTop: 20 }}
						name={'verificationNotes'}
						initialValue={data?.verificationNotes}
						rules={groupName === 'CCA' ? [rule.required()] : []}
					>
						<TextArea
							rows={3}
							disabled={isReadOnly || groupName !== 'CCA'}
						/>
					</Form.Item>
				)}

				<Button
					type="primary"
					htmlType="submit"
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
						marginTop: 40,
					}}
					disabled={
						submitNAPBackgroundInfoMutation.isPending ||
						verifyNAPBackgroundInfoMutation.isPending ||
						isReadOnly
					}
					loading={submitNAPBackgroundInfoMutation.isPending}
				>
					NEXT
				</Button>
			</Form>
		</Fragment>
	);
};

export default NAPApplicationBG;
